import { CoverageCheckLayoutConfig } from '../../components/flows/layouts/CoverageCheckConsents';
import { CoverageResultsLayoutConfig } from '../../components/flows/layouts/CoverageResultsLayout';
import { getCoverageAddressRoute, getCoverageBenefitsRoute } from '../../util/CoverageSelectors';
import {
  CoverageScreenName,
  MedicareAccessScreenName,
  ScreenConfig,
  ScreenType,
} from '../FlowTypes';
import {
  COVERAGE_BENEFIT_NONE,
  COVERAGE_BENEFIT_NOT_SURE,
  COVERAGE_PLAN_TYPE_EMPLOYER,
  COVERAGE_PLAN_TYPE_MEDICAID,
  COVERAGE_PLAN_TYPE_NOT_SURE,
  COVERAGE_PLAN_TYPE_SELF_PURCHASED,
  HEALTH_COVER_MEDICARE_ADVANTAGE,
  HEALTH_COVER_MEDICARE_ORIGINAL,
} from '../InsuranceProviders';
import { MEDICARE_ACCESS_STATE_LAYOUT } from './MedicareAccessConfig';
import { NEXT_BUTTON_TITLE } from './NervaControlScreenNames';

/**
 * US under-65 insurance coverage screens (MIS-1), shared by the Control and HCP-referred Personalise
 * configs. Entered from the insurance-provider dropdown via `getCoverageEntryRoute`; the address
 * screen exits to `CoverageResults` (Original Medicare without an address answers the state question
 * first), which each config declares itself because the onward route differs (Control → Auth,
 * Personalise → referral/payment map). Screen names double as `user_data` keys, so they are stable;
 * stored plan-type values come from `InsuranceProviders.ts`.
 */
export const COVERAGE_SCREENS: Array<
  ScreenConfig<CoverageScreenName | MedicareAccessScreenName.MedicareAccessStateNoAddress>
> = [
  {
    screenName: CoverageScreenName.CoveragePlanType,
    type: ScreenType.SingleSelect,
    layout: {
      body: ['How do you get your health insurance?'],
      choices: [
        {
          value: COVERAGE_PLAN_TYPE_EMPLOYER,
          label: 'Through my employer (or a family member’s employer)',
        },
        { value: COVERAGE_PLAN_TYPE_SELF_PURCHASED, label: 'I bought it myself' },
        HEALTH_COVER_MEDICARE_ORIGINAL,
        HEALTH_COVER_MEDICARE_ADVANTAGE,
        COVERAGE_PLAN_TYPE_MEDICAID,
        { value: COVERAGE_PLAN_TYPE_NOT_SURE, label: 'I’m not sure' },
      ],
    },
    // Medicare / Medicaid users skip the employer and benefits questions and go straight to the
    // address; the results screen (and isMedicareOriginal) pick the ACCESS pathway up from there.
    nextScreen: {
      [COVERAGE_PLAN_TYPE_EMPLOYER]: CoverageScreenName.CoverageUsEmployer,
      [COVERAGE_PLAN_TYPE_SELF_PURCHASED]: CoverageScreenName.CoverageBenefits,
      [COVERAGE_PLAN_TYPE_NOT_SURE]: CoverageScreenName.CoverageBenefits,
      [HEALTH_COVER_MEDICARE_ORIGINAL]: CoverageScreenName.CoverageAddress,
      [HEALTH_COVER_MEDICARE_ADVANTAGE]: CoverageScreenName.CoverageAddress,
      [COVERAGE_PLAN_TYPE_MEDICAID]: CoverageScreenName.CoverageAddress,
      fallback: CoverageScreenName.CoverageAddress,
    },
  },
  {
    screenName: CoverageScreenName.CoverageUsEmployer,
    type: ScreenType.CoverageEmployer,
    layout: {
      title: 'Who is your employer?',
      body: [
        'Your employer usually decides the benefits list, so this is the most useful thing you can tell us.',
      ],
      inputLabel: 'Company name',
      inputPlaceholder: 'Company name',
      authorization: {
        label:
          'Also ask my employer to cover Nerva. I authorize Nerva to tell my employer’s benefits team and its benefits broker or consultant my name and that I asked for Nerva to be covered. Optional. Once shared, federal health privacy law no longer protects it and they may share it further. Lasts 12 months; withdraw any time.',
        showFullTitle: 'Show full authorization',
        hideFullTitle: 'Hide full authorization',
        fullText: [
          'I authorize Mindset Health Inc. ("Nerva") to share my name, my employer, and the fact that I asked for Nerva (a gut-brain program for IBS and related gut conditions) to be covered.',
          'With my employer’s health benefits and HR teams, and the benefits brokers, consultants and plan administrators who help my employer choose or run its benefits, to: tell them I asked for Nerva; confirm my request is genuine if they ask; answer their questions about it; and ask them to add or purchase Nerva.',
          'Term and revocation: valid for 12 months from today. I can revoke it any time by emailing privacy@mindsethealth.com. Revocation doesn’t affect messages already sent.',
          'My rights: optional, and doesn’t affect my access to Nerva or my price. Nerva won’t sell this information. My employer and its advisers aren’t bound by federal health privacy law once they receive it, and may share it further.',
          'Ticking the box is my electronic signature. I can ask for a copy of this authorization at privacy@mindsethealth.com.',
        ],
      },
      primaryButtonTitle: NEXT_BUTTON_TITLE,
    },
    nextScreen: CoverageScreenName.CoverageBenefits,
  },
  {
    screenName: CoverageScreenName.CoverageBenefits,
    type: ScreenType.MultiSelect,
    layout: {
      body: ['Do you have any of these benefits?'],
      choices: [
        'HSA (Health Savings Account)',
        'FSA (Flexible Spending Account)',
        'Wellness stipend from my employer',
        COVERAGE_BENEFIT_NONE,
        COVERAGE_BENEFIT_NOT_SURE,
      ],
      exclusiveChoices: [COVERAGE_BENEFIT_NONE, COVERAGE_BENEFIT_NOT_SURE],
      primaryButtonTitle: NEXT_BUTTON_TITLE,
    },
    // Branch by how the user arrived (plan type), not by the benefits picked.
    nextScreen: {
      employer: CoverageScreenName.CoverageWorkEmail,
      fallback: CoverageScreenName.CoverageAddress,
    },
    nextScreenSelector: getCoverageBenefitsRoute,
  },
  {
    screenName: CoverageScreenName.CoverageWorkEmail,
    type: ScreenType.SingleInput,
    layout: {
      type: 'email',
      collectsPIIData: true,
      content: [
        'What’s your work email?',
        'We’ll email you a message you can forward straight to your benefits team.',
        'A work address makes it a one-tap ask.',
      ],
      inputLabel: 'Work email',
      inputPlaceholder: 'your@email.com',
      primaryButtonTitle: NEXT_BUTTON_TITLE,
    },
    nextScreen: CoverageScreenName.CoverageAddress,
  },
  {
    screenName: CoverageScreenName.CoverageAddress,
    type: ScreenType.CoverageAddress,
    layout: {
      title: 'What’s your address?',
      addressLabel: 'Street address',
      addressPlaceholder: 'Start typing your address',
      notFoundButtonTitle: 'I can’t find my address',
      infoBox: {
        titleText: 'Why do we ask this?',
        bodyText:
          'Your health plan needs your address to confirm your coverage and process any claims. Your information is private and secure.',
      },
      primaryButtonTitle: NEXT_BUTTON_TITLE,
    },
    nextScreen: {
      askState: MedicareAccessScreenName.MedicareAccessStateNoAddress,
      fallback: CoverageScreenName.CoverageResults,
    },
    nextScreenSelector: getCoverageAddressRoute,
  },
  {
    // The address state decides ACCESS, so Original Medicare users who couldn't find their address
    // are asked for it here; the results screen then makes the ACCESS decision.
    screenName: MedicareAccessScreenName.MedicareAccessStateNoAddress,
    type: ScreenType.DropdownSelect,
    layout: MEDICARE_ACCESS_STATE_LAYOUT,
    nextScreen: CoverageScreenName.CoverageResults,
  },
];

/**
 * "Check my coverage" variant of the insurance-provider dropdown, applied on the Control and
 * Personalise entry screens for US non-65+ users. Both consents are required before the button
 * enables; the self-pay link skips the coverage flow (see getCoverageEntryRoute / hasOptedForSelfPay).
 */
export const COVERAGE_CHECK_LAYOUT: CoverageCheckLayoutConfig = {
  title: 'Now let’s check if you are covered by your insurance.',
  question: 'Who’s your health insurance provider?',
  primaryButtonTitle: 'Check my coverage',
  authorization: {
    label:
      'Help me get Nerva covered. I authorize Nerva to check my coverage, contact my health plan on my behalf, and use my request to ask my plan to cover Nerva. Withdraw any time.',
    showFullTitle: 'Show full authorization',
    hideFullTitle: 'Hide full authorization',
    fullText: [
      'I authorize Mindset Health Inc. ("Nerva") to use and share my name, date of birth, address, insurance details, the fact that I use Nerva (a gut-brain program for IBS and related gut conditions), coverage information my plan returns, and clinical documentation from my referring clinician, including a letter of medical necessity. Psychotherapy notes are excluded.',
      'With my health plan and its administrators, to: verify eligibility through Nerva’s clearinghouse partner; request coverage, predeterminations, authorizations and exceptions; submit claims and appeals and act as my representative where the plan permits; and ask my plan to cover Nerva, including letting it evaluate Nerva for its members.',
      'Term and revocation: valid while I have a Nerva account. I can revoke it any time by emailing privacy@mindsethealth.com. Revocation doesn’t affect actions already taken in reliance on it.',
      'My rights: optional, and doesn’t affect my access to Nerva or my price. Nerva won’t sell this information or use it for advertising. My plan remains bound by federal health privacy law; information may be redisclosed by recipients as their rules allow.',
      'Ticking the box is my electronic signature.',
    ],
  },
  assignmentOfBenefits: {
    label:
      'If my plan ever pays for Nerva, pay Nerva directly. I assign any benefits payable for Nerva to Mindset Health Inc. and authorize it to receive payment from my plan and to submit claims and predeterminations with my signature on file. I pay Nerva’s listed price; if my plan pays Nerva for a period I’ve already paid for, Nerva refunds me.',
  },
  selfPayTitle: 'I’ll pay for Nerva myself',
};

/**
 * Coverage results shown after the address step. Shared so both configs show the same copy while
 * each keeps its own onward route and points the assistance card at its PAP screens.
 */
export const COVERAGE_RESULTS_LAYOUT: CoverageResultsLayoutConfig = {
  title: 'You may not be covered yet.',
  subtitle: 'Plans decide what they cover, and lists change when members ask.',
  nextSteps: {
    heading: 'What happens next',
    body: [
      'We’ll email you a ready-to-send request for your insurer, so you can forward it whenever works for you.',
      'There’s nothing you need to do right now.',
    ],
    emailLead: 'It’ll go to',
  },
  assistance: {
    heading: 'Other financial assistance',
    title: 'Patient Assistance Program',
    body: 'Means-tested support that can reduce the cost, in some cases to nothing.',
    linkTitle: 'See how',
  },
  primaryButtonTitle: 'Start free trial',
};
